import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import { MeetingStatus } from "../../../types";

interface MeetingTimeProgressProps {
  startTime: string | Date;
  endTime: string | Date;
}

const MeetingTimeProgress: React.FC<MeetingTimeProgressProps> = ({
  startTime,
  endTime,
}) => {
  const theme = useTheme();
  const now = Date.now();
  const startTimeMs = new Date(startTime).getTime();
  const endTimeMs = new Date(endTime).getTime();
  const isStartInPast = startTimeMs <= now;
  const isEndInPast = endTimeMs <= now;
  const isInProgress = isStartInPast && !isEndInPast;
  const durationMs = Math.max(endTimeMs - startTimeMs, 0);
  const elapsedRatio =
    durationMs === 0
      ? isEndInPast
        ? 1
        : 0
      : Math.min(Math.max((now - startTimeMs) / durationMs, 0), 1);
  const progressLineHeight = `${elapsedRatio * 100}%`;
  const completedColor = "#64748b";
  const activeColor = theme.palette.meetingStatus[MeetingStatus.Started].main;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "9px minmax(86px, auto)",
        gridTemplateRows: "auto 1fr auto",
        columnGap: 0.8,
        alignSelf: "stretch",
        height: "100%",
        minHeight: 30,
      }}
    >
      <Box
        sx={{
          gridColumn: 1,
          gridRow: 1,
          width: 9,
          height: 9,
          borderRadius: "50%",
          alignSelf: "center",
          justifySelf: "center",
          backgroundColor: isStartInPast
            ? isInProgress
              ? activeColor
              : completedColor
            : "transparent",
          border: isStartInPast ? "none" : `1.5px solid ${completedColor}`,
          boxSizing: "border-box",
        }}
      />
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={800}
        sx={{
          gridColumn: 2,
          gridRow: 1,
          lineHeight: 1.1,
          alignSelf: "center",
        }}
      >
        {format(new Date(startTime), "h:mm a")}
      </Typography>
      <Box
        sx={{
          gridColumn: 1,
          gridRow: 2,
          width: "1px",
          minHeight: 12,
          height: "calc(100% + 8px)",
          position: "relative",
          top: "-4px",
          borderRadius: 999,
          justifySelf: "center",
          backgroundColor: "#94a3b8",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "-1px",
            right: "-1px",
            height: progressLineHeight,
            borderRadius: 999,
            backgroundColor: isInProgress ? activeColor : completedColor,
          }}
        />
      </Box>
      <Box
        sx={{
          gridColumn: 1,
          gridRow: 3,
          width: 9,
          height: 9,
          borderRadius: "50%",
          alignSelf: "center",
          justifySelf: "center",
          backgroundColor: isEndInPast ? completedColor : "transparent",
          border: isEndInPast ? "none" : `1.5px solid ${completedColor}`,
          boxSizing: "border-box",
        }}
      />
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={500}
        sx={{
          gridColumn: 2,
          gridRow: 3,
          lineHeight: 1.1,
          alignSelf: "center",
        }}
      >
        {format(new Date(endTime), "h:mm a")}
      </Typography>
    </Box>
  );
};

export default MeetingTimeProgress;
